import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const functionPlotConfigSchema = z.object({
  expression: z.string().min(1).max(240).default('sin(x)'),
  xMin: z.number().finite().default(-10),
  xMax: z.number().finite().default(10),
  yMin: z.number().finite().default(-2),
  yMax: z.number().finite().default(2),
  samples: z.number().int().min(40).max(2000).default(400),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#2563eb'),
  showAxes: z.boolean().default(true),
  points: z.array(z.object({
    x: z.number().finite(),
    y: z.number().finite(),
    label: z.string().default(''),
  }).strict()).max(1000).default([]),
  referenceLines: z.array(z.object({
    axis: z.enum(['x', 'y']),
    value: z.number().finite(),
    label: z.string().default(''),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#94a3b8'),
  }).strict()).max(100).default([]),
  extraExpressions: z.array(z.object({
    expression: z.string().min(1).max(240),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#0891b2'),
    label: z.string().default(''),
  }).strict()).max(10).default([]),
  criticalPoints: z.array(z.object({
    x: z.number().finite(),
    label: z.string().default(''),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  }).strict()).max(50).default([]),
}).strict().refine(value => value.xMax > value.xMin, { message: 'x 轴范围无效' }).refine(value => value.yMax > value.yMin, { message: 'y 轴范围无效' });

export const functionPlotDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.function-plot@1',
    category: 'formula',
    title: '函数图像',
    description: '通过受控数学表达式解析器采样并显示二维函数。',
    capabilities: ['function-plot', 'math-expression', 'responsive-svg', 'multi-function'],
    suitableFor: ['函数性质', '参数变化', '理论曲线', '多函数比较'],
    inputs: [],
    outputs: [],
    events: [],
    constraints: [
      '不接受 JavaScript',
      '表达式长度不超过 240 字符',
      '采样点不超过 2000',
    ],
    configSchemaId: 'probfun.function-plot.config@1',
    configSchema: {
      type: 'object',
      required: ['expression', 'xMin', 'xMax', 'yMin', 'yMax'],
      properties: {
        expression: { type: 'string', minLength: 1, maxLength: 240 },
        xMin: { type: 'number' },
        xMax: { type: 'number' },
        yMin: { type: 'number' },
        yMax: { type: 'number' },
        samples: { type: 'integer', minimum: 40, maximum: 2000 },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
        showAxes: { type: 'boolean', default: true },
        points: {
          type: 'array',
          maxItems: 1000,
          items: {
            type: 'object',
            required: ['x', 'y'],
            properties: {
              x: { type: 'number' },
              y: { type: 'number' },
              label: { type: 'string' },
            },
            additionalProperties: false,
          },
        },
        referenceLines: {
          type: 'array',
          maxItems: 100,
          items: {
            type: 'object',
            required: ['axis', 'value'],
            properties: {
              axis: { type: 'string', enum: ['x', 'y'] },
              value: { type: 'number' },
              label: { type: 'string' },
              color: {
                type: 'string',
                pattern: '^#[0-9a-fA-F]{6}$',
              },
            },
            additionalProperties: false,
          },
        },
        extraExpressions: {
          type: 'array',
          maxItems: 10,
          default: [],
          items: {
            type: 'object',
            required: ['expression'],
            properties: {
              expression: { type: 'string', minLength: 1, maxLength: 240 },
              color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
              label: { type: 'string' },
            },
          },
        },
        criticalPoints: {
          type: 'array',
          maxItems: 50,
          default: [],
          items: {
            type: 'object',
            required: ['x'],
            properties: {
              x: { type: 'number' },
              label: { type: 'string' },
              color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
            },
          },
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      expression: 'sin(x)',
      xMin: -10,
      xMax: 10,
      yMin: -2,
      yMax: 2,
      samples: 400,
      color: '#2563eb',
      showAxes: true,
      points: [],
      referenceLines: [],
    },
    examples: [{
      expression: 'exp(-x^2 / 2) / sqrt(2 * pi)',
      xMin: -4,
      xMax: 4,
      yMin: 0,
      yMax: 0.45,
      samples: 500,
      color: '#2563eb',
      showAxes: true,
      points: [{ x: 0, y: 0.3989, label: '峰值' }],
      referenceLines: [{
        axis: 'x',
        value: 0,
        label: '均值',
        color: '#94a3b8',
      }],
    }, {
      expression: 'sin(x)',
      xMin: -6.28,
      xMax: 6.28,
      yMin: -1.5,
      yMax: 1.5,
      samples: 400,
      color: '#2563eb',
      showAxes: true,
      points: [{ x: 0, y: 0, label: '原点' }],
      referenceLines: [],
      extraExpressions: [{
        expression: 'cos(x)',
        color: '#0891b2',
        label: 'cos(x)',
      }],
    }],
    notes: ['表达式由 mathjs 解析，不进入 Vue 或 JavaScript 编译器。', '支持多函数叠加（extraExpressions）和关键点标注（criticalPoints）。', 'referenceLines 可绘制 x 方向或 y 方向的参考线。'],
    layout: {
      ...componentHardMinimum('probfun.function-plot@1'),
      maxWidth: 1200,
      preferredAspectRatio: 1.6,
    },
    runtimeRequirements: [],
    metadata: {
      layoutHints: {
        roles: ['analysis'],
        preferredSize: 'medium',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: false,
        priority: 45,
      },
    },
  },
  configSchema: functionPlotConfigSchema,
  loader: () => import('./FunctionPlotBlock.vue').then(module => module.default),
};

// formula.display
const formulaDisplayConfigSchema = z.object({
  latex: z.string().max(500).default(''),
  displayMode: z.boolean().default(true),
}).strict();

export const formulaDisplayDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.formula-display@1',
    category: 'formula',
    title: '公式展示',
    description: '安全渲染 LaTeX 数学公式，展示符号表达式而非计算曲线。',
    capabilities: ['formula-display', 'latex-render'],
    suitableFor: ['数学公式', '定理表达式', '分布密度函数'],
    inputs: [],
    outputs: [],
    events: [],
    constraints: [
      '禁止 href/def/newcommand 等危险命令',
      '公式长度不超过 500 字符',
      '使用 KaTeX 或降级文本渲染',
    ],
    configSchemaId: 'probfun.formula-display.config@1',
    configSchema: {
      type: 'object',
      properties: {
        latex: { type: 'string', maxLength: 500, default: '' },
        displayMode: { type: 'boolean', default: true },
      },
      additionalProperties: false,
    },
    defaultConfig: { latex: 'P(A|B) = \\frac{P(B|A) \\cdot P(A)}{P(B)}', displayMode: true },
    examples: [
      { latex: 'P(A|B) = \\frac{P(B|A) \\cdot P(A)}{P(B)}', displayMode: true },
      { latex: 'f(x) = \\frac{1}{\\sqrt{2\\pi\\sigma^2}} e^{-\\frac{(x-\\mu)^2}{2\\sigma^2}}', displayMode: true },
    ],
    notes: ['与 function-plot 的区别：formula-display 展示符号公式，function-plot 画函数曲线。'],
    layout: componentHardMinimum('probfun.formula-display@1'),
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['explanation'], preferredSize: 'small', supportsCompact: true, supportsExpanded: true, canStack: true, priority: 25 } },
  },
  configSchema: formulaDisplayConfigSchema,
  loader: () => import('./FormulaDisplayBlock.vue').then(module => module.default),
};
